import { useIsFocused } from 'expo-router';
import { useEffect, useSyncExternalStore } from 'react';
import { BackHandler, StyleSheet, View as RNView } from 'react-native';
import { YStack } from 'tamagui';

import { color, space } from '@/constants/tokens';
import PSButton from './PSButton';
import PSText from './PSText';

/**
 * PSModal — a blocking notice with exactly one way out (atom).
 *
 * The card the purchase screens throw when the answer is "you can't buy this":
 * a title, the reason, and a single button that leaves. Same light card as web's
 * [[PSAlertHost]], so the app has one dialog look.
 *
 * Undismissable on purpose — no backdrop press, and Android's back button is
 * swallowed. The screen under it has nothing left to offer, so the CTA is the
 * only path that makes sense.
 *
 * NOT RN's `Modal`. That one is a native presentation, and iOS silently drops
 * one requested while the screen is still dismissing a native sheet — which is
 * exactly when a purchase page learns the answer (login lands mid auth flow,
 * and the page refocuses the moment that sheet starts to close). Instead this
 * component only registers what to show, and [[PSModalHost]], a sibling of the
 * root Stack, draws it over every screen and its native header. No presentation,
 * so nothing to lose.
 *
 * Registered only while its screen is FOCUSED, so a screen buried under another
 * can't paint over the one on top. The flip side: the host renders UNDER a
 * natively-presented route (the auth sheet), so this can't be used from inside
 * one — the same limit the root toast host has.
 *
 * Fixed props, no `children`: every call site today is title + reason + one
 * button. A second action lands when a call site needs it.
 */

type ModalContent = {
  title: string;
  message: string;
  ctaTitle: string;
  onPressCta: () => void;
};

// One slot: a screen shows at most one of these, and only the focused screen
// can register.
let shown: ModalContent | null = null;
const listeners = new Set<() => void>();

const show = (next: ModalContent | null) => {
  shown = next;
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export default function PSModal({
  visible,
  title,
  message,
  ctaTitle,
  onPressCta,
}: ModalContent & { visible: boolean }) {
  const isShown = useIsFocused() && visible;

  useEffect(() => {
    if (!isShown) return;
    const back = BackHandler.addEventListener('hardwareBackPress', () => true);
    return () => {
      back.remove();
      show(null);
    };
  }, [isShown]);

  useEffect(() => {
    if (isShown) show({ title, message, ctaTitle, onPressCta });
  }, [isShown, title, message, ctaTitle, onPressCta]);

  return null;
}

/** Where a registered [[PSModal]] draws. Mounted once, after the root Stack. */
export function PSModalHost() {
  const content = useSyncExternalStore(
    subscribe,
    () => shown,
    () => null
  );

  if (!content) return null;

  return (
    <RNView style={[StyleSheet.absoluteFill, styles.scrim]}>
      <YStack
        bg={'$color.background'}
        rounded={'$base'}
        p={'$space.lg'}
        gap={'$space.lg'}
      >
        <YStack gap={'$space.xs'}>
          <PSText.H2 color={'$color.textDark'}>{content.title}</PSText.H2>
          <PSText.Body2 color={'$color.textDarkMuted'}>
            {content.message}
          </PSText.Body2>
        </YStack>
        <PSButton
          fullWidth
          title={content.ctaTitle}
          onPress={content.onPressCta}
        />
      </YStack>
    </RNView>
  );
}

const styles = StyleSheet.create({
  // Plain RN with absoluteFill, as in PSAlertHost: pure positioning over the
  // app column (so on web the card stays column-wide), values off the tokens.
  scrim: {
    justifyContent: 'center',
    paddingHorizontal: space.xl,
    backgroundColor: color.scrim,
  },
});
